import React from 'react';
import { ArrowLeft, Shield, Database, Eye, Lock } from 'lucide-react';

const PrivacyPolicy = ({ onNavigate }) => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black text-gray-100 p-4 font-poppins">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <button 
                        onClick={() => onNavigate('landing')}
                        className="inline-flex items-center text-zinc-400 hover:text-white mb-6 transition-colors"
                    >
                        <ArrowLeft size={16} className="mr-2" />
                        Back to Home
                    </button>
                    <h1 className="text-4xl font-bold text-white mb-4 flex items-center gap-3">
                        <Shield className="text-purple-400" size={36} />
                        Privacy Policy
                    </h1>
                    <p className="text-gray-400">Last updated: January 8, 2025</p>
                </div>

                {/* Content */}
                <div className="bg-zinc-900/50 border border-zinc-700 rounded-xl p-8 space-y-8">
                    
                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4 flex items-center gap-2">
                            <Database size={24} />
                            1. Information We Collect
                        </h2>
                        <div className="text-gray-300 leading-relaxed space-y-4">
                            <p><strong>Account Information:</strong> When you create an account, we collect your email address and encrypted password.</p>
                            
                            <p><strong>Project Data:</strong> We store the credits, settings, and media files you create and upload to our Service.</p>
                            
                            <p><strong>Usage Information:</strong> We collect information about how you use our Service, including features accessed, export frequency, and session duration.</p>
                            
                            <p><strong>Technical Information:</strong> We automatically collect IP addresses, browser type, device information, and other technical data for security and performance purposes.</p>
                            
                            <p><strong>Payment Information:</strong> Payment processing is handled by Stripe. We do not store your credit card information on our servers.</p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4 flex items-center gap-2">
                            <Eye size={24} />
                            2. How We Use Your Information
                        </h2>
                        <p className="text-gray-300 leading-relaxed mb-4">We use your information to:</p>
                        <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                            <li>Provide and maintain our Service</li>
                            <li>Process your payments and manage subscriptions</li>
                            <li>Store and sync your projects across devices</li>
                            <li>Send important account and service notifications</li>
                            <li>Provide customer support</li>
                            <li>Improve our Service through analytics and usage patterns</li>
                            <li>Detect and prevent fraud or abuse</li>
                            <li>Comply with legal obligations</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">3. Information Sharing and Disclosure</h2>
                        <div className="text-gray-300 leading-relaxed space-y-4">
                            <p><strong>We do not sell your personal information.</strong> We may share your information in these limited circumstances:</p>
                            
                            <p><strong>Service Providers:</strong> We use trusted third-party services including:</p>
                            <ul className="list-disc list-inside ml-4 space-y-1">
                                <li>Google Firebase (hosting, authentication, database)</li>
                                <li>Stripe (payment processing)</li>
                                <li>Cloud storage providers (file storage and backups)</li>
                            </ul>
                            
                            <p><strong>Legal Requirements:</strong> We may disclose information if required by law, court order, or to protect our rights and safety.</p>
                            
                            <p><strong>Business Transfers:</strong> If our company is acquired or merged, user information may be transferred as part of that transaction.</p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">4. Data Security</h2>
                        <div className="text-gray-300 leading-relaxed space-y-4">
                            <p>We implement industry-standard security measures to protect your information:</p>
                            <ul className="list-disc list-inside ml-4 space-y-2">
                                <li>SSL/TLS encryption for data transmission</li>
                                <li>Encrypted data storage</li>
                                <li>Regular security audits and updates</li>
                                <li>Limited access controls for our team</li>
                                <li>Secure authentication systems</li>
                            </ul>
                            <p className="text-yellow-300">
                                <strong>Note:</strong> While we implement strong security measures, no system is 100% secure. 
                                We cannot guarantee absolute security of your information.
                            </p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">5. Your Privacy Rights</h2>
                        <div className="text-gray-300 leading-relaxed space-y-4">
                            <p><strong>Access:</strong> You can access and download your account information and project data at any time through your account settings.</p>
                            
                            <p><strong>Correction:</strong> You can update your account information directly in your profile settings.</p>
                            
                            <p><strong>Deletion:</strong> You can delete your account and all associated data by contacting us at support@blackbox-is.com. We will process deletion requests within 30 days.</p>
                            
                            <p><strong>Data Portability:</strong> You can export your project data in standard formats.</p>
                            
                            <p><strong>Marketing Communications:</strong> You can opt out of promotional emails at any time using the unsubscribe link.</p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">6. Data Retention</h2>
                        <div className="text-gray-300 leading-relaxed space-y-4">
                            <p><strong>Active Accounts:</strong> We retain your data as long as your account is active and for the duration of your subscription.</p>
                            
                            <p><strong>Deleted Accounts:</strong> After account deletion, we remove personal data within 30 days, though some information may be retained longer for legal or legitimate business purposes.</p>
                            
                            <p><strong>Export Files:</strong> Video exports are automatically deleted from our servers after 30 days.</p>
                            
                            <p><strong>Backups:</strong> Data in automated backups may persist for up to 90 days after deletion.</p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">7. Cookies and Tracking</h2>
                        <div className="text-gray-300 leading-relaxed space-y-4">
                            <p>We use cookies and similar technologies for:</p>
                            <ul className="list-disc list-inside ml-4 space-y-2">
                                <li>Authentication and account security</li>
                                <li>Remembering your preferences and settings</li>
                                <li>Analytics to improve our Service</li>
                                <li>Fraud prevention and security</li>
                            </ul>
                            <p>You can control cookies through your browser settings, though this may affect Service functionality.</p>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">8. International Data Transfers</h2>
                        <p className="text-gray-300 leading-relaxed">
                            Our Service is hosted in the United States. If you access our Service from outside the US, 
                            your information may be transferred to, stored, and processed in the US where our servers are located.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">9. Children's Privacy</h2>
                        <p className="text-gray-300 leading-relaxed">
                            Our Service is not intended for children under 13. We do not knowingly collect personal information 
                            from children under 13. If we learn we have collected such information, we will delete it promptly.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">10. California Privacy Rights</h2>
                        <div className="text-gray-300 leading-relaxed space-y-4">
                            <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>
                            <ul className="list-disc list-inside ml-4 space-y-2">
                                <li>Right to know what personal information we collect and how it's used</li>
                                <li>Right to delete personal information</li>
                                <li>Right to opt-out of the sale of personal information (we don't sell your information)</li>
                                <li>Right to non-discrimination for exercising your privacy rights</li>
                            </ul>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4">11. Changes to This Policy</h2>
                        <p className="text-gray-300 leading-relaxed">
                            We may update this Privacy Policy periodically. We will notify you of material changes via email 
                            or through a prominent notice in our Service. Your continued use of the Service after changes 
                            constitutes acceptance of the updated Privacy Policy.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold text-purple-400 mb-4 flex items-center gap-2">
                            <Lock size={24} />
                            12. Contact Us
                        </h2>
                        <div className="text-gray-300 leading-relaxed">
                            <p className="mb-4">If you have questions about this Privacy Policy or our privacy practices, please contact us:</p>
                            <div className="bg-zinc-800/50 border border-zinc-600 rounded-lg p-4">
                                <p><strong>Email:</strong> <a href="mailto:support@blackbox-is.com" className="text-purple-400 hover:text-purple-300">support@blackbox-is.com</a></p>
                                <p><strong>Company:</strong> BLACKBOXis</p>
                                <p><strong>Location:</strong> Florida, USA</p>
                                <p><strong>Subject Line:</strong> Privacy Policy Inquiry</p>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;